import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { ApiError } from '../api/client.ts';

type ToastMsg = { id: number; text: string; tone: 'ok' | 'bad' };
const ToastContext = createContext<{ ok: (t: string) => void; fail: (e: unknown) => void } | null>(null);

/** One polite live region for success and failure notices. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastMsg[]>([]);
  const push = useCallback((text: string, tone: ToastMsg['tone']) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, tone }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 5000);
  }, []);
  const api = {
    ok: useCallback((t: string) => push(t, 'ok'), [push]),
    fail: useCallback((e: unknown) => push(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Something went wrong', 'bad'), [push]),
  };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-20 z-50 flex flex-col items-center gap-2 px-4 lg:bottom-6">
        {items.map((t) => (
          <div key={t.id} className="pointer-events-auto flex max-w-md items-center gap-2 rounded-lg bg-ink px-4 py-3 text-sm text-white shadow-lg">
            {t.tone === 'ok' ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-300" aria-hidden /> : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-300" aria-hidden />}
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
